import { RedirectToSignIn, UserButton } from '@clerk/clerk-react';
import { IS_DEV_AUTH, useDevAuth } from '../../hooks/useDevAuth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GradingIcon from '@mui/icons-material/Grading';
import SummarizeIcon from '@mui/icons-material/Summarize';
import LabelIcon from '@mui/icons-material/Label';
import MenuIcon from '@mui/icons-material/Menu';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PeopleIcon from '@mui/icons-material/People';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PolicyIcon from '@mui/icons-material/Policy';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import WorkIcon from '@mui/icons-material/Work';
import {
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import Loading from '../Loading';

const drawerWidth = 240;
const collapsedDrawerWidth = 60;

interface NavItem {
  text: string;
  icon?: React.ReactNode;
  path?: string;
  divider?: boolean;
  section?: boolean;
}

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const location = useLocation();
  const { isSignedIn } = useDevAuth();
  const { currentUser, isLoading, error: userError } = useCurrentUser();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    setDesktopCollapsed(!desktopCollapsed);
  };

  const currentDrawerWidth = desktopCollapsed ? collapsedDrawerWidth : drawerWidth;

  const menuItems: NavItem[] = [
    { text: 'Admin', section: true },
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
    { text: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
    { text: 'Criteria', icon: <GradingIcon />, path: '/admin/criteria' },
    {
      text: 'Policy Eligibility',
      icon: <GradingIcon />,
      path: '/admin/policy-comparison',
    },
    { text: 'Transcriptions', icon: <FormatAlignLeftIcon />, path: '/admin/transcriptions' },
    { text: 'Labels', icon: <LabelIcon />, path: '/admin/labels' },
    { text: 'Assignments', icon: <WorkIcon />, path: '/admin/assignments' },
    {
      text: 'Labels overview',
      icon: <SummarizeIcon />,
      path: '/admin/ground-truth-coverage',
    },
    {
      text: 'Marking metrics',
      icon: <AssessmentIcon />,
      path: '/admin/marking-metrics',
    },
    { divider: true, text: 'divider' },
    { text: 'Monitoring', section: true },
    {
      text: 'Resolve disagreements',
      icon: <SportsMmaIcon />,
      path: '/admin/disagreements',
    },
    {
      text: 'Review outputs',
      icon: <RateReviewIcon />,
      path: '/admin/output-review',
    },
    {
      text: 'View reviewed outputs',
      icon: <DoneAllIcon />,
      path: '/admin/reviewed-outputs',
    },
    {
      text: 'Policy suggestions',
      icon: <PolicyIcon />,
      path: '/admin/policy-suggestions',
    },
    { divider: true, text: 'divider' },
    { text: 'Moderation', section: true },
    {
      text: 'Review Moderation',
      icon: <FactCheckIcon />,
      path: '/admin/moderation',
    },
    {
      text: 'Moderation Policy Suggestions',
      icon: <PolicyIcon />,
      path: '/admin/moderation-suggestions',
    },
    { divider: true, text: 'divider' },
    { text: 'Labelling', section: true },
    { text: 'Labelling Interface', icon: <NoteAddIcon />, path: '/' },
  ];

  if (!isSignedIn) return IS_DEV_AUTH ? null : <RedirectToSignIn />;

  if (isLoading) return <Loading />;

  if (userError) return <Navigate to="/" />;

  if (currentUser?.userType !== 'admin') return <Navigate to="/" />;

  const drawer = (isCollapsed: boolean = false) => (
    <Box>
      <Toolbar sx={{ justifyContent: isCollapsed ? 'center' : 'space-between' }}>
        {!isCollapsed && (
          <Typography variant="h6" noWrap component="div">
            Admin Panel
          </Typography>
        )}
        {!IS_DEV_AUTH && !isCollapsed && <UserButton />}
        {!IS_DEV_AUTH && isCollapsed && (
          <Tooltip title="Admin Panel" placement="right">
            <Box>
              <UserButton />
            </Box>
          </Tooltip>
        )}
      </Toolbar>
      <Divider />
      {isCollapsed ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <IconButton onClick={handleDesktopToggle} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      ) : (
        <ListItemButton onClick={handleDesktopToggle} sx={{ py: 1, px: 2 }}>
          <ListItemIcon>
            <ChevronLeftIcon />
          </ListItemIcon>
          <ListItemText primary="Collapse Sidebar" />
        </ListItemButton>
      )}
      <Divider />
      <List>
        {menuItems.map((item, index) =>
          item.divider ? (
            <Divider key={`divider-${index}`} sx={{ my: 1 }} />
          ) : item.section ? (
            isCollapsed ? (
              <Divider key={`section-${index}`} sx={{ my: 1 }} />
            ) : (
              <ListItem key={item.text} disablePadding sx={{ px: 2, py: 1 }}>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    variant: 'overline',
                    color: 'text.secondary',
                  }}
                />
              </ListItem>
            )
          ) : (
            <ListItem key={item.text} disablePadding>
              {isCollapsed ? (
                <Tooltip title={item.text} placement="right">
                  <ListItemButton
                    component={Link as any}
                    to={item.path || ''}
                    selected={location.pathname === item.path}
                    sx={{ justifyContent: 'center', px: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 'auto' }}>{item.icon}</ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton
                  component={Link as any}
                  to={item.path || ''}
                  selected={location.pathname === item.path}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              )}
            </ListItem>
          )
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Box component="nav" sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: currentDrawerWidth },
          }}
          open
        >
          {drawer(desktopCollapsed)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
        }}
      >
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ display: { sm: 'none' }, mb: 1 }}
        >
          <MenuIcon />
        </IconButton>
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
